import React, { useState, useEffect } from 'react';
import { Typography, Box, Paper, Card, Grid, TextField, Button } from '@mui/material';
import dayjs from 'dayjs';

const fixedTargetDate = dayjs('2016-10-27T08:00:00');
const defaultDate = dayjs('2025-03-01T09:00:00+08:00'); // HKT timezone

const timeBox = {
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  p: { xs: 1, sm: 3 },  // Even smaller padding on mobile (xs: 1 instead of 1.5)
  m: { xs: 0.25, sm: 1 },
  minWidth: { xs: '80px', sm: '120px' }, // Slightly narrower min-width on mobile
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'all 0.3s ease-in-out',
  border: '2px solid',
  borderColor: 'pink'
};

const formatDate = (date) => {
  return dayjs(date).format('DD-MMM-YYYY');
};

const TimeDisplay = ({ title, timeData }) => (
  <Card elevation={12} sx={{ 
    p: { xs: 1.5, sm: 4 }, // Reduced padding on mobile (xs: 1.5 instead of 2)
    mt: 4, 
    borderRadius: 4, 
  }}>
    <Typography 
      component="h1" 
      variant="h5" 
      gutterBottom 
      align="center" 
      sx={{ mb: 1.5, color: 'primary.main', whiteSpace: 'pre-line', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} // Slightly smaller text on mobile
    >
      {title}
    </Typography>
    <Grid 
      container 
      justifyContent="center" 
      spacing={{ xs: 0.5, sm: 2 }}
    >
      {Object.entries(timeData).map(([interval, value]) => (
        <Grid item xs={6} sm="auto" key={interval}>
          <Box sx={timeBox}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                mb: 1,
                animation: 'bounce 1s infinite',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-5px)' }
                }
              }}
            >
              🐽
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'pink' }}>
              {String(value).padStart(2, '0')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'pink', textTransform: 'capitalize' }}>
              {interval}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Card>
);

function CountdownTimer() {
  const [customDate, setCustomDate] = useState(defaultDate);
  const [timeLeft, setTimeLeft] = useState({})
  const [customTimeLeft, setCustomTimeLeft] = useState({})
  const [showDaysOnly, setShowDaysOnly] = useState(true)

  function calculateTimeLeft(targetDate, isCustomTimer = false) {
    try {
      const now = dayjs()
      const target = dayjs(targetDate)
      const isFuture = target.isAfter(now)
      const difference = isFuture ? 
        target.diff(now, 'millisecond') : 
        now.diff(target, 'millisecond')

      if (showDaysOnly) {
        const totalDays = Math.floor(difference / (1000 * 60 * 60 * 24))
        return {
          days: totalDays,
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24)
        }
      }

      if (isCustomTimer && !showDaysOnly) {
        return {
          years: Math.floor(difference / (1000 * 60 * 60 * 24 * 365)),
          months: Math.floor((difference % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30.44)),
          days: Math.floor((difference / (1000 * 60 * 60 * 24)) % 30.44),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        }
      }

      return {
        years: Math.floor(difference / (1000 * 60 * 60 * 24 * 365)),
        months: Math.floor((difference % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30.44)),
        days: Math.floor((difference / (1000 * 60 * 60 * 24)) % 30.44),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24)
      }
    } catch (error) {
      console.error('Error calculating time:', error)
      return showDaysOnly ? 
        { days: 0, hours: 0 } :
        isCustomTimer ?
          { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 } :
          { years: 0, months: 0, days: 0, hours: 0 }
    }
  }

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(fixedTargetDate, false))
    setCustomTimeLeft(calculateTimeLeft(customDate, true))

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(fixedTargetDate, false))
      setCustomTimeLeft(calculateTimeLeft(customDate, true))
    }, 1000)

    return () => clearInterval(timer)
  }, [customDate, showDaysOnly])

  return (
    <Box>
      <TimeDisplay 
        title={`Time elapsed since ${formatDate(fixedTargetDate)} 💍` } 
        timeData={timeLeft} 
      />

      <Card elevation={12} sx={{ p: 4, mt: 4, borderRadius: 4, backgroundColor: 'background.paper' }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            type="datetime-local"
            fullWidth
            label="Select Target Date and Time"
            InputLabelProps={{ shrink: true }}
            value={customDate.format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => setCustomDate(dayjs(e.target.value))}
            sx={{ mb: 2 }}
          />
        </Box>
        
        <Typography 
          component="h1" 
          variant="h5" 
          gutterBottom 
          align="center"
          sx={{ 
            mb: 2, 
            color: 'primary.main',
            whiteSpace: 'pre-line'  // Add this to allow \n line breaks
          }}
        >
          {`Time ${customDate.isAfter(dayjs()) ? 'remaining until' : 'elapsed since'}\n${formatDate(customDate)}${customDate.isSame(defaultDate) ? ' 🥇🐭' : ''}`}
        </Typography>
        
        <Grid container justifyContent="center" spacing={{ xs: 0.5, sm: 2 }}>
          {Object.entries(customTimeLeft).map(([interval, value]) => (
            <Grid item xs={6} sm="auto" key={interval}>
              <Box sx={{ ...timeBox, borderColor: 'primary.main' }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {value}
                </Typography>
                <Typography variant="h6" sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                  {interval}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>
      <Button 
        variant="contained"
        onClick={() => setShowDaysOnly(!showDaysOnly)}
        sx={{ 
          mt: 2,
          width: '100%'
        }}
      >
        {showDaysOnly ? 'Show Detail' : 'Days Only'}
      </Button>
    </Box>
  );
}

export default CountdownTimer;